import { useQuery, useQueryClient } from "@tanstack/react-query";
import { boronClient } from "../../api";
import { createError } from "../../errors";
import { paths } from "../../lib/api/v1";

export type AiAssistantTemplateInterface =
  paths["/api/v1/sections/{section_id}/ai_assistant_templates"]["get"]["responses"]["200"]["content"]["application/json"]["aiAssistantTemplates"][number];

const path = "/api/v1/sections/{section_id}/ai_assistant_templates" as const;

const buildQueryKey = (sectionId: string) => [path, sectionId];

export const useFetchAiAssistantTemplates = ({
  sectionId,
}: {
  sectionId: string;
}) => {
  return useQuery<AiAssistantTemplateInterface[]>({
    queryKey: buildQueryKey(sectionId),
    queryFn: async () => {
      const { response, data } = await boronClient.GET(path, {
        params: { path: { section_id: sectionId } },
      });
      if (response.ok && data) {
        return data.aiAssistantTemplates;
      }
      throw await createError(response);
    },
  });
};

export const useAiAssistantTemplatesCacheUpdater = () => {
  const queryClient = useQueryClient();

  const invalidateAiAssistantTemplates = (sectionId: string) => {
    queryClient.resetQueries({ queryKey: buildQueryKey(sectionId) });
  };

  const updateAiAssistantTemplate = (
    sectionId: string,
    newTemplate: AiAssistantTemplateInterface,
  ) => {
    queryClient.setQueryData<AiAssistantTemplateInterface[]>(
      buildQueryKey(sectionId),
      (oldData) => {
        if (!oldData) {
          return [];
        }
        return oldData.map((template) =>
          template.id === newTemplate.id ? newTemplate : template,
        );
      },
    );
  };

  const removeAiAssistantTemplate = (sectionId: string, templateId: number) => {
    queryClient.setQueryData<AiAssistantTemplateInterface[]>(
      buildQueryKey(sectionId),
      (oldData) => {
        if (!oldData) {
          return [];
        }
        return oldData.filter((template) => template.id !== templateId);
      },
    );
  };

  return {
    invalidateAiAssistantTemplates,
    updateAiAssistantTemplate,
    removeAiAssistantTemplate,
  };
};
